import React, { useState } from 'react';
import { Flag } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { reportsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

export const ReportModal = ({ open, onOpenChange, entry, category }) => {
    const { user } = useAuth();
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) {
            toast.error('Please describe the issue');
            return;
        }

        setSubmitting(true);
        try {
            await reportsApi.create({
                entry_id: entry.id,
                entry_title: entry.title,
                category: category,
                message: message.trim(),
                reporter_id: user.discord_id,
                reporter_name: user.username,
            });
            toast.success('Report submitted successfully');
            setMessage('');
            onOpenChange(false);
        } catch (error) {
            console.error('Failed to submit report:', error);
            toast.error('Failed to submit report');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md bg-[#0A0A0A] border-white/10">
                <DialogHeader>
                    <DialogTitle className="font-['Rajdhani'] text-xl text-white flex items-center gap-2">
                        <Flag className="text-red-400" size={20} />
                        Report Issue
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-xs text-gray-500 mb-1">Reporting issue for:</p>
                        <p className="text-white font-medium">{entry?.title}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase bg-white/5 text-gray-400">
                            {category}
                        </span>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="message" className="text-gray-400">
                            Describe the issue *
                        </Label>
                        <Textarea
                            id="message"
                            data-testid="report-message-input"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Please describe which values are incorrect and what the correct values should be..."
                            rows={5}
                            required
                            className="bg-black/40 border-white/10 resize-none"
                        />
                        <p className="text-xs text-gray-600">
                            Be specific about which values are wrong and provide correct information if possible.
                        </p>
                    </div>

                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting || !message.trim()}
                            data-testid="submit-report-button"
                            className="bg-red-500/80 hover:bg-red-500 text-white font-semibold"
                        >
                            {submitting ? 'Submitting...' : 'Submit Report'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
